// ABOUTME: Pure utility functions for FamilyInsightsPage headline insight computation
// ABOUTME: Computes heart day rate insights from trend data — no API calls, no side effects

import { getTodayInFamilyTimezone, getDateInFamilyTimezone, daysBackFrom } from './dateUtils';

export type InsightsTabLabel = 'Week' | 'Month' | '3 Months' | '6 Months' | 'All Time';

export interface TabDateRange {
  startDate: string;
  endDate: string;
  granularity: 'day' | 'week' | 'month';
  isDayGranularity: boolean;
}

export interface HeartDayDataPoint {
  period: string;
  heartDayCount: number;
  totalDays: number;
  successRate: number;
}

export interface InsightResult {
  emoji: string;
  message: string;
}

const TAB_DAYS_BACK: Record<string, number | null> = {
  'Week': 7,
  'Month': 30,
  '3 Months': 90,
  '6 Months': 180,
  'All Time': null,
};

const TAB_GRANULARITY: Record<string, 'day' | 'week' | 'month'> = {
  'Week': 'day',
  'Month': 'day',
  '3 Months': 'week',
  '6 Months': 'week',
  'All Time': 'month',
};

const TAB_IS_DAY: Record<string, boolean> = {
  'Week': true,
  'Month': true,
  '3 Months': false,
  '6 Months': false,
  'All Time': false,
};

const TAB_PERIOD_LABEL: Record<string, string> = {
  'Week': 'week',
  'Month': 'month',
  '3 Months': '3 months',
  '6 Months': '6 months',
  'All Time': 'all time',
};

export function computeTabDateRange(
  tabLabel: string,
  familyCreatedAt: string | undefined,
  familyTimezone: string | undefined,
): TabDateRange {
  const today = getTodayInFamilyTimezone(familyTimezone);
  const daysBack = TAB_DAYS_BACK[tabLabel];
  const granularity = TAB_GRANULARITY[tabLabel] ?? 'day';
  const isDayGranularity = TAB_IS_DAY[tabLabel] ?? false;

  let startDate: string;
  if (daysBack === null) {
    // All Time: use family creation date
    if (familyCreatedAt) {
      startDate = getDateInFamilyTimezone(new Date(familyCreatedAt), familyTimezone);
    } else {
      // Fallback: 365 days
      startDate = daysBackFrom(today, 364);
    }
  } else {
    startDate = daysBackFrom(today, daysBack - 1);
  }

  return { startDate, endDate: today, granularity, isDayGranularity };
}

export function computeHeartDayInsight(
  childName: string,
  data: HeartDayDataPoint[],
  currentStreak: number,
  tabLabel: string,
): InsightResult {
  const fallback: InsightResult = {
    emoji: '',
    message: `Keep logging to unlock insights for ${childName}.`,
  };

  if (data.length < 14) return fallback;

  // Streak takes priority — 3+ consecutive heart days
  if (currentStreak >= 3) {
    return {
      emoji: '⭐',
      message: `${childName} has a ${currentStreak}-day heart streak.`,
    };
  }

  // Improvement story: compare first half vs second half average success rate
  const midpoint = Math.floor(data.length / 2);
  const firstHalf = data.slice(0, midpoint);
  const secondHalf = data.slice(midpoint);

  const avg = (points: HeartDayDataPoint[]) =>
    points.reduce((sum, p) => sum + p.successRate, 0) / points.length;

  const firstAvg = avg(firstHalf);
  const secondAvg = avg(secondHalf);

  if (firstAvg > 0) {
    const pctChange = ((secondAvg - firstAvg) / firstAvg) * 100;
    if (pctChange >= 10) {
      const roundedPct = Math.round(pctChange);
      const periodLabel = TAB_PERIOD_LABEL[tabLabel] ?? 'recently';
      return {
        emoji: '⭐',
        message: `${childName}'s heart day rate is up ${roundedPct}% from last ${periodLabel}.`,
      };
    }
  }

  return fallback;
}

export function computePositivityRatio(
  stars: number,
  triangles: number,
  squares: number,
): string {
  const negatives = triangles + squares;
  if (negatives === 0) return 'N/A';
  const ratio = stars / negatives;
  // Format: if whole number show as integer, otherwise 1 decimal
  const formatted = Number.isInteger(ratio) ? ratio.toString() : ratio.toFixed(1);
  return `${formatted}:1`;
}
